import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AdminPage } from '../../../components/design-system/admin/AdminPage';
import { PillTabs } from '../../../components/design-system/admin/PillTabs';
import { RequestsTab } from '../../../modules/vendor/shopper-requests/RequestsTab';
import { RequestDrawer } from '../../../modules/vendor/shopper-requests/RequestDrawer';
import { SettingsTab } from '../../../modules/vendor/shopper-requests/SettingsTab';
import type { RequestDetail, RequestView } from '../../../modules/vendor/shopper-requests/types';

type Tab = 'requests' | 'settings';

/**
 * Sales → Requests: what shoppers asked for — a sample, a showroom visit, a
 * quote, a call back, an order — with their room and their products, and the
 * reply to send. `?request=<uuid>` opens one (the link notifications carry);
 * `?tab=settings` is where the vendor decides what shoppers can ask for and
 * lists their stores.
 */
export const ShopperRequestsPage = () => {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const tab: Tab = params.get('tab') === 'settings' ? 'settings' : 'requests';
    const requestUuid = params.get('request');
    const [row, setRow] = useState<RequestView | null>(null);
    const [patched, setPatched] = useState<RequestDetail | null>(null);

    const setParam = (key: string, value: string | null, replace = true) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                return next;
            },
            { replace },
        );

    const openRequest = (r: RequestView) => {
        setRow(r);
        setParam('request', r.uuid, false);
    };

    return (
        <AdminPage
            title={t('R2R.NAV.REQUESTS')}
            description={t('SHOPPER_REQUESTS.SUBTITLE')}
            parents={[{ label: t('R2R.WORKSPACE.SALES') }]}
            className="mx-auto w-full max-w-[1100px]"
        >
            <PillTabs<Tab>
                value={tab}
                onChange={(v) => setParam('tab', v === 'settings' ? 'settings' : null)}
                ariaLabel={t('R2R.NAV.REQUESTS')}
                className="self-start"
                options={[
                    { value: 'requests', label: t('SHOPPER_REQUESTS.TABS.REQUESTS') },
                    { value: 'settings', label: t('SHOPPER_REQUESTS.TABS.SETTINGS') },
                ]}
            />

            {tab === 'settings' ? (
                <SettingsTab />
            ) : (
                <RequestsTab onOpen={openRequest} onOpenSettings={() => setParam('tab', 'settings')} patched={patched} />
            )}

            <RequestDrawer
                uuid={requestUuid}
                initial={row}
                onClose={() => setParam('request', null)}
                onChanged={setPatched}
                onOpenOther={(uuid) => setParam('request', uuid, false)}
            />
        </AdminPage>
    );
};

export default ShopperRequestsPage;
